import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../api";

function SummaryTable({ summary, onPayAll, busy }) {
  if (!summary || summary.length === 0) return <div className="empty">No commissions yet.</div>;
  return (
    <div className="table-wrap">
      <table data-testid="promoter-summary-table">
        <thead><tr><th>Currency</th><th>Pending</th><th>Payable gross</th><th>Clawback</th><th>Currently owed</th><th>Paid</th><th>Lifetime</th><th></th></tr></thead>
        <tbody>
          {summary.map((s) => (
            <tr key={s.currency} data-testid={`summary-row-${s.currency}`}>
              <td><code>{s.currency}</code></td><td>{s.pending}</td><td>{s.payableGross}</td>
              <td>{s.outstandingClawback}</td><td>{s.netCurrentlyOwed}</td><td>{s.paid}</td><td>{s.lifetime}</td>
              <td><button className="btn btn-primary btn-sm" disabled={busy || Number(s.payableGross) <= 0}
                onClick={() => onPayAll(s)} data-testid={`pay-all-${s.currency}`}>Mark all paid</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PromoterDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [detail, payouts] = await Promise.all([
        api.get(`/promoters/${id}`), api.get(`/promoters/${id}/payouts`),
      ]);
      setData({ ...detail.data, payouts: payouts.data });
    } catch (_) {
      setError("Affiliate portal account not found");
    }
  };

  const confirmation = (currency, gross, clawback) => {
    const applied = Math.min(Number(gross), Math.abs(Math.min(Number(clawback), 0)));
    const net = Math.max(Number(gross) - applied, 0).toFixed(2);
    return window.confirm(
      `Confirm payout\nCurrency: ${currency}\nGross commissions: ${gross}\n` +
      `Outstanding clawback applied: -${applied.toFixed(2)}\nExpected net payout: ${net}`
    );
  };

  const payCommission = async (commission) => {
    const row = (data.summary || []).find((s) => s.currency === commission.currency) || {};
    if (!confirmation(commission.currency, commission.commissionAmount, row.outstandingClawback || "0")) return;
    setBusy(true); setError("");
    try { await api.post(`/commissions/${commission.id}/pay`); await load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const payAll = async (row) => {
    if (!confirmation(row.currency, row.payableGross, row.outstandingClawback)) return;
    setBusy(true); setError("");
    try { await api.post(`/promoters/${id}/pay-all-payable`, { currency: row.currency }); await load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [id]);

  const resetPassword = async () => {
    setError("");
    try {
      await api.put(`/promoters/${id}`, { newPassword: pwd });
      setPwd("");
      await load();
      alert("Password reset. The affiliate must change it at next login.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (error) return <div className="empty" data-testid="promoter-error">{error}</div>;
  if (!data) return <div className="loading">Loading…</div>;

  const { user, referral, rules, commissions, summary, payouts } = data;

  return (
    <div data-testid="promoter-detail-page">
      <header className="page-head">
        <div>
          <Link to="/promoters" className="back-link">← Affiliate Portal Access</Link>
          <div className="muted small">Affiliate Portal Access</div>
          <h1>{user.email}</h1>
        </div>
        <span className={"badge " + (user.status === "active" ? "badge-green" : "badge-gray")}>{user.status}</span>
      </header>

      <div className="detail-grid">
        <div><span className="muted">Affiliate</span><div>{referral?.name || "—"}</div></div>
        <div><span className="muted">Referral Code</span><div><code>{referral?.code || "—"}</code></div></div>
        <div><span className="muted">Default %</span><div>{referral?.defaultRate ?? "—"}</div></div>
        <div><span className="muted">Referral Link</span><div className="ref-url">{referral?.referralUrl || "—"}</div></div>
      </div>

      <h2 className="section-title">Commission Summary (by currency)</h2>
      <SummaryTable summary={summary} onPayAll={payAll} busy={busy} />

      <h2 className="section-title">Special Commission Rules</h2>
      {referral?.affiliateId && (
        <Link className="btn btn-ghost btn-sm" to={`/affiliates/${referral.affiliateId}`}>Manage commission rules</Link>
      )}
      {(!rules || rules.length === 0) ? (
        <div className="empty">No special rules assigned.</div>
      ) : (
        <div className="table-wrap">
          <table data-testid="promoter-rules-table">
            <thead><tr><th>Scope</th><th>Target</th><th>Title</th><th>Rate %</th></tr></thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                  <td>{r.scope}</td><td><code>{r.targetId}</code></td><td>{r.targetTitle || "—"}</td><td>{r.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="section-title">Commission History</h2>
      {(!commissions || commissions.length === 0) ? (
        <div className="empty" data-testid="promoter-commissions-empty">No commission entries.</div>
      ) : (
        <div className="table-wrap">
          <table data-testid="promoter-commissions-table">
            <thead>
              <tr><th>Order</th><th>Date</th><th>Product</th><th>Base</th><th>%</th><th>Commission</th><th>Cur</th><th>Maturity</th><th>Payment</th><th></th></tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                  <td>{c.orderNumber}</td>
                  <td>{c.orderDate ? new Date(c.orderDate).toLocaleDateString() : "—"}</td>
                  <td>{c.productTitle}</td>
                  <td>{c.commissionableAmount}</td>
                  <td>{c.appliedRate ?? "—"}</td>
                  <td>{c.commissionAmount}</td>
                  <td>{c.currency}</td>
                  <td><span className="badge badge-yellow">{c.maturityStatus}</span></td>
                  <td><span className="badge badge-gray">{c.paymentStatus}</span></td>
                  <td>{c.maturityStatus === "payable" && c.paymentStatus === "unpaid" && Number(c.commissionAmount) > 0 && (
                    <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => payCommission(c)}
                      data-testid={`pay-commission-${c.id}`}>Mark paid</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="section-title">Payout History</h2>
      {(!payouts || payouts.length === 0) ? <div className="empty">No payouts yet.</div> : (
        <div className="table-wrap"><table data-testid="payout-history-table">
          <thead><tr><th>Paid</th><th>Currency</th><th>Gross</th><th>Clawbacks</th><th>Net paid</th><th>Commissions</th><th>Payout ID</th></tr></thead>
          <tbody>{payouts.map((p) => <tr key={p.id}>
            <td>{new Date(p.paidAt).toLocaleString()}</td><td>{p.currency}</td>
            <td>{p.grossCommissionAmount}</td><td>{p.adjustmentAmount}</td><td>{p.netPaidAmount}</td>
            <td>{p.commissionCount}</td><td><code>{p.id}</code></td>
          </tr>)}</tbody>
        </table></div>
      )}

      <h2 className="section-title">Reset Password</h2>
      <div style={{ display: "flex", gap: 8, maxWidth: 480 }}>
        <input data-testid="reset-password-input" type="text" placeholder="New initial password (min 8)"
          value={pwd} onChange={(e) => setPwd(e.target.value)} style={{ flex: 1 }} />
        <button className="btn btn-primary" data-testid="reset-password-btn" onClick={resetPassword} disabled={pwd.length < 8}>
          Reset
        </button>
      </div>
    </div>
  );
}
